import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Descriptions, Spin, message, Empty, Table, Typography } from "antd";
import { ArrowLeftOutlined, EditOutlined, LinkOutlined, FolderOutlined, TeamOutlined, EyeOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";
import { ProjectDetailsWrapper } from "./Projects.style";
import ProjectRecommendations from "./ProjectRecommendations";

const { Text } = Typography;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  rejected: "red",
  completed: "default",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  rejected: "Rejected",
  completed: "Completed",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "₹0";
  return `₹ ${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const jobStatusColors = {
  draft: "default",
  pending_approval: "orange",
  open: "blue",
  in_progress: "cyan",
  filled: "green",
  on_hold: "gold",
  closed: "default",
  cancelled: "red",
};

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hiringRecords, setHiringRecords] = useState([]);

  const fetchProject = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_PROJECT}/${id}?includeRelated=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data.project);
    } catch (error) {
      console.error("Error fetching project:", error);
      message.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchHiringRecords = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        { status: "active" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiringRecords(response.data.hiringRecords || []);
    } catch (error) {
      console.error("Error fetching hiring records:", error);
    }
  }, []);

  useEffect(() => {
    fetchProject();
    fetchHiringRecords();
  }, [fetchProject, fetchHiringRecords]);

  const jobColumns = [
    {
      title: "Job ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code style={{ fontSize: 12 }}>{id}</Text>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => <Text strong>{role}</Text>,
    },
    {
      title: "Experience",
      dataIndex: "experienceRange",
      key: "experienceRange",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={jobStatusColors[status] || "default"}>
          {status?.replace("_", " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Positions",
      dataIndex: "noOfOpenings",
      key: "noOfOpenings",
      width: 90,
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/customer/my-jobs/${record.id}`)}
        />
      ),
    },
  ];

  const projectJobIds = new Set((project?.jobs || []).map((j) => j.id));
  const projectHiringRecords = hiringRecords.filter((hr) => projectJobIds.has(hr.jobId));
  const projectStats = {
    activeContracts: projectHiringRecords.length,
    activeTalents: new Set(projectHiringRecords.map((hr) => hr.talentId)).size,
    monthlySpend: projectHiringRecords.reduce((s, hr) => s + (parseFloat(hr.monthlyRate) || 0), 0),
    totalBilled: projectHiringRecords.reduce((s, hr) => s + (parseFloat(hr.totalBilled) || 0), 0),
  };
  const projectJobGroups = Object.values(
    projectHiringRecords.reduce((acc, hr) => {
      if (!acc[hr.jobId]) acc[hr.jobId] = { jobId: hr.jobId, jobTitle: hr.jobTitle, records: [] };
      acc[hr.jobId].records.push(hr);
      return acc;
    }, {})
  );

  if (loading) {
    return (
      <ProjectDetailsWrapper>
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      </ProjectDetailsWrapper>
    );
  }

  if (!project) {
    return (
      <ProjectDetailsWrapper>
        <Empty description="Project not found" />
      </ProjectDetailsWrapper>
    );
  }

  return (
    <ProjectDetailsWrapper>
      <div className="header">
        <div className="header-left">
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/customer/projects")}
          />
          <div>
            <p className="project-id-badge">{project.id}</p>
            <h2>{project.title}</h2>
          </div>
        </div>
        <div className="header-actions">
          <Tag color={statusColors[project.status]} style={{ fontSize: 14, padding: "4px 12px" }}>
            {statusLabels[project.status]}
          </Tag>
          {(project.status === "pending_approval" || project.status === "rejected") && (
            <Button 
              icon={<EditOutlined />}
              onClick={() => navigate(`/customer/edit-project/${project.id}`)}
            >
              {project.status === "rejected" ? "Edit & Resubmit" : "Edit"}
            </Button>
          )}
        </div>
      </div>

      {project.status === "rejected" && project.rejectionReason && (
        <div style={{
          background: "#fff2f0",
          border: "1px solid #ffccc7",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 24,
          display: "flex",
          alignItems: "flex-start",
          gap: 12
        }}>
          <span style={{ color: "#cf1322", fontSize: 20 }}>⚠</span>
          <div>
            <div style={{ fontWeight: 600, color: "#cf1322", marginBottom: 4 }}>Rejection Reason</div>
            <div style={{ color: "#595959" }}>{project.rejectionReason}</div>
          </div>
        </div>
      )}

      {project.engagement && (
        <div 
          className="engagement-link-banner"
          onClick={() => navigate(`/customer/engagement/${project.engagementId}`)}
        >
          <LinkOutlined style={{ fontSize: 20, color: "#1890ff" }} />
          <div className="engagement-info">
            <div className="label">Parent Engagement</div>
            <div className="title">{project.engagement.title}</div>
          </div>
          <Tag color={project.engagement.status === "active" ? "green" : "orange"}>
            {project.engagement.status}
          </Tag>
        </div>
      )}

      {project.status === "active" && project.health && (
        <Row gutter={16} className="status-cards">
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <div className="status-label">Overall Health</div>
              <div className={`status-value ${project.health.overall}`}>
                {project.health.overall === "good" && "Good"}
                {project.health.overall === "warning" && "Warning"}
                {project.health.overall === "critical" && "Critical"}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <div className="status-label">Timeline</div>
              <div className={`status-value ${project.health.timeline}`}>
                {project.health.timeline === "good" && "On Track"}
                {project.health.timeline === "warning" && "Ending Soon"}
                {project.health.timeline === "critical" && "Overdue"}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="status-card">
              <div className="status-label">Budget</div>
              <div className={`status-value ${project.health.budget}`}>
                {project.health.budget === "good" && "Within Budget"}
                {project.health.budget === "warning" && "80%+ Used"}
                {project.health.budget === "critical" && "Exceeded"}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {project.status === "rejected" && project.rejectionReason && (
        <Card style={{ marginBottom: 24, background: "#fff2f0", borderColor: "#ffccc7" }}>
          <div style={{ color: "#cf1322", fontWeight: 500, marginBottom: 8 }}>Rejection Reason</div>
          <p style={{ margin: 0 }}>{project.rejectionReason}</p>
        </Card>
      )}

      <Card style={{ marginBottom: 24 }}>
        <div className="details-section">
          <div className="section-title">Project Information</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="label">Budget Allocated</div>
              <div className="value">{formatCurrency(project.budgetAllocated)}</div>
            </div>
            <div className="info-item">
              <div className="label">Budget Spent</div>
              <div className="value">{formatCurrency(project.budgetSpent)}</div>
            </div>
            <div className="info-item">
              <div className="label">Expected Start</div>
              <div className="value">{formatDate(project.expectedStartDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Expected End</div>
              <div className="value">{formatDate(project.expectedEndDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Actual Start</div>
              <div className="value">{formatDate(project.actualStartDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Actual End</div>
              <div className="value">{formatDate(project.actualEndDate)}</div>
            </div>
            <div className="info-item">
              <div className="label">Total Jobs</div>
              <div className="value">{project.jobCount || 0}</div>
            </div>
            <div className="info-item">
              <div className="label">Total Hired</div>
              <div className="value">{project.totalHired || 0}</div>
            </div>
          </div>
        </div>

        {project.description && (
          <div className="details-section">
            <div className="section-title">Description</div>
            <p style={{ margin: 0, color: "#595959" }}>{project.description}</p>
          </div>
        )}

        {project.primaryTechnologies && project.primaryTechnologies.length > 0 && (
          <div className="details-section">
            <div className="section-title">Technical Stack</div>
            <div style={{ marginBottom: 12 }}>
              <strong>Primary Technologies:</strong>{" "}
              {project.primaryTechnologies.map((tech, i) => (
                <Tag key={i} color="blue">{tech}</Tag>
              ))}
            </div>
            {project.secondaryTechnologies && project.secondaryTechnologies.length > 0 && (
              <div>
                <strong>Secondary Technologies:</strong>{" "}
                {project.secondaryTechnologies.map((tech, i) => (
                  <Tag key={i} color="cyan">{tech}</Tag>
                ))}
              </div>
            )}
          </div>
        )}

        {project.architectureType && (
          <div className="details-section">
            <div className="section-title">Project Metrics</div>
            <Row gutter={16}>
              <Col span={6}>
                <div className="info-item">
                  <div className="label">Architecture</div>
                  <div className="value" style={{ textTransform: "capitalize" }}>{project.architectureType}</div>
                </div>
              </Col>
              <Col span={6}>
                <div className="info-item">
                  <div className="label">Delivery Type</div>
                  <div className="value" style={{ textTransform: "capitalize" }}>{project.deliveryType || "-"}</div>
                </div>
              </Col>
              <Col span={6}>
                <div className="info-item">
                  <div className="label">Complexity</div>
                  <div className="value">{project.complexityLevel}%</div>
                </div>
              </Col>
              <Col span={6}>
                <div className="info-item">
                  <div className="label">Maturity Index</div>
                  <div className="value">{project.maturityIndex}/5</div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Card>

      {projectHiringRecords.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 600, color: "#014c75", marginBottom: 20 }}>
            <TeamOutlined /> Ongoing Activity
            <Tag color="blue" style={{ marginLeft: 4, fontWeight: 600 }}>{projectStats.activeContracts} contract{projectStats.activeContracts !== 1 ? "s" : ""}</Tag>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
            {[
              { label: "Active Contracts", value: String(projectStats.activeContracts), sub: `${projectJobGroups.length} job${projectJobGroups.length !== 1 ? "s" : ""}` },
              { label: "Active Talents", value: String(projectStats.activeTalents), sub: "working now" },
              { label: "Monthly Spend", value: `₹ ${parseFloat(projectStats.monthlySpend).toLocaleString("en-IN")}`, sub: "per month", small: true },
              { label: "Total Billed", value: `₹ ${parseFloat(projectStats.totalBilled).toLocaleString("en-IN")}`, sub: "all time", small: true },
            ].map((item, i) => (
              <div key={i} style={{ background: "linear-gradient(135deg, #f8fdff 0%, #f0f9ff 100%)", border: "1px solid #e6f4ff", borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>{item.label}</div>
                <div style={{ fontSize: item.small ? 20 : 28, fontWeight: 700, color: "#014c75", lineHeight: 1, marginBottom: 4 }}>{item.value}</div>
                <div style={{ fontSize: 12, color: "#01d9a9", fontWeight: 500 }}>{item.sub}</div>
              </div>
            ))}
          </div>

          <Table
            dataSource={projectHiringRecords}
            rowKey="id"
            size="small"
            pagination={projectHiringRecords.length > 8 ? { pageSize: 8 } : false}
            columns={[
              {
                title: "Talent", dataIndex: "name", key: "name",
                render: (n) => <Text strong>{n}</Text>,
              },
              { title: "Experience", dataIndex: "experience", key: "exp", width: 110 },
              {
                title: "Job Role", dataIndex: "jobTitle", key: "job",
                render: (t, r) => (
                  <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => navigate(`/customer/my-jobs/${r.jobId}`)}>
                    {t}
                  </Button>
                ),
              },
              {
                title: "Rate / mo", dataIndex: "monthlyRate", key: "rate", width: 130,
                render: (r) => `₹ ${parseFloat(r || 0).toLocaleString("en-IN")}`,
              },
              {
                title: "Total Billed", dataIndex: "totalBilled", key: "billed", width: 130,
                render: (b) => <Text strong style={{ color: "#014c75" }}>₹ {parseFloat(b || 0).toLocaleString("en-IN")}</Text>,
              },
              {
                title: "Start Date", dataIndex: "onboardingDate", key: "start", width: 110,
                render: (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "-",
              },
              { title: "Duration", dataIndex: "contractDuration", key: "dur", width: 100 },
              {
                title: "Expiry", dataIndex: "daysLeft", key: "days", width: 130,
                render: (d) => (
                  <Tag color={d <= 0 ? "red" : d <= 30 ? "orange" : d <= 90 ? "gold" : "green"}>
                    {d <= 0 ? "Expired" : d <= 30 ? `${d}d — soon` : `${d}d left`}
                  </Tag>
                ),
              },
            ]}
          />
        </Card>
      )}

      {(project.status === "active" || project.status === "pending_approval") && 
        (project.creationMode === "STAFF_AUGMENTATION"
          ? true
          : (project.primaryTechnologies && project.primaryTechnologies.length > 0)) && (
        <ProjectRecommendations 
          projectId={project.id} 
          projectStatus={project.status}
          project={project}
        />
      )}

      <Card className="jobs-section" style={{ marginTop: 24 }}>
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: '#014c75' }}>
            <TeamOutlined /> Jobs ({project.jobs?.length || 0})
          </div>
        </div>

        {project.jobs && project.jobs.length > 0 ? (
          <Table
            className="job-table"
            columns={jobColumns}
            dataSource={project.jobs}
            rowKey="id"
            pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="small"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, background: '#fafafa', borderRadius: 12, color: '#8c8c8c' }}>
            <TeamOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No jobs created under this project</div>
            <Text type="secondary">Jobs will appear here once created for this project</Text>
          </div>
        )}
      </Card>
    </ProjectDetailsWrapper>
  );
};

export default ProjectDetails;
